import * as swcHelpers from '@swc/helpers';
import { getLocalizedAttrValues, getLocalizedAttrValue } from '@grandvisionhq/utils';

// PRODUCT
function getFrameData(product) {
    const gender = getLocalizedAttrValues(product.masterVariant, 'gender', 'en');
    // Check if it's a kids glasses
    const isKidsGlasses = gender.some((a)=>a.toLowerCase() === 'kids'
    );
    const framePrice = product.masterVariant.prices && product.masterVariant.prices[0].value.centAmount || 0;
    const brand = getLocalizedAttrValue(product.masterVariant, 'brand', 'en');
    return {
        brand,
        framePrice,
        gender,
        isKidsGlasses
    };
}
// LENS PACKS
function getLensPacks(product, isKids) {
    const attributes = product.masterVariant.attributes;
    const lensDetails = attributes && attributes.find((attr)=>attr.name === 'lensDetails'
    );
    if (!lensDetails) {
        return {
            monofocalLensPacks: [],
            multifocalLensPacks: []
        };
    }
    function movePackNameUp(lensPack) {
        const packType = (lensPack.attributes || []).find((attr)=>attr.name === 'pack'
        ).value.key;
        return swcHelpers.objectSpread({}, lensPack, {
            packType: packType || ''
        });
    }
    const lensPacksData = lensDetails.value.obj.masterData.current;
    const includedLensPack = movePackNameUp(swcHelpers.objectSpread({}, lensPacksData.masterVariant, {
        isIncluded: true
    }));
    const lensPacks = lensPacksData.variants.map((pack)=>movePackNameUp(swcHelpers.objectSpread({}, pack, {
            isIncluded: false
        }))
    );
    const monofocalLensPacks = lensPacks.filter((pack)=>{
        const lens = pack.attributes.find((attr)=>attr.name === 'lens'
        );
        return lens.value.key === 'singleVision';
    });
    monofocalLensPacks.unshift(includedLensPack);
    let multifocalLensPacks = [];
    if (!isKids) {
        multifocalLensPacks = lensPacks.filter((pack)=>{
            const lens = pack.attributes.find((attr)=>attr.name === 'lens'
            );
            return lens.value.key === 'multifocal';
        });
    }
    return {
        includedLensPack,
        monofocalLensPacks,
        multifocalLensPacks
    };
}
// CALCULATIONS
function calcGlassesPrice(opticalData, glassesType) {
    if (glassesType === 'frameAndMonofocal') {
        return opticalData.frameAndMonofocalPrice;
    }
    if (glassesType === 'frameAndMultifocal') {
        return opticalData.frameAndMultifocalPrice;
    }
    return opticalData.framePrice;
}
/**
 * Calculates the +/- price for a selected glasses type compared to the initial type.
 */ function calcGlassesPriceDifference(opticalData, glassesType, initialGlassesType = 'frameAndMonofocal') {
    const priceMapping = {
        frameOnly: opticalData.framePrice,
        frameAndMonofocal: opticalData.frameAndMonofocalPrice,
        frameAndMultifocal: opticalData.frameAndMultifocalPrice
    };
    return priceMapping[glassesType] - priceMapping[initialGlassesType];
}
function calcLensPackPrice(opticalData, glassesType, lensPack, useIncludedLensPack = false) {
    const { brand , isKidsGlasses  } = opticalData;
    let lensPackPrice = 0;
    if (glassesType === 'frameOnly') {
        return lensPackPrice;
    }
    const { monofocalLensPacks , multifocalLensPacks  } = opticalData;
    const activePackList = glassesType === 'frameAndMonofocal' ? monofocalLensPacks : multifocalLensPacks;
    const selectedPack = activePackList.find((pack)=>pack.packType === lensPack
    );
    if (!selectedPack) {
        return lensPackPrice;
    }
    if (!useIncludedLensPack) {
        var ref;
        return (ref = getPriceOfLensPack(selectedPack)) !== null && ref !== void 0 ? ref : 0;
    }
    if (opticalData.includedLensPack && opticalData.includedLensPack.packType === lensPack && glassesType === 'frameAndMonofocal') {
        // if the lensPack is already included, then set lensPackPrice to 0
        lensPackPrice = 0;
    } else if (glassesType === 'frameAndMonofocal' && lensPack === 'basic' && (brand.toLowerCase() === 'seen' || isKidsGlasses)) {
        // 'basic' is included on Seen and kids glasses --> when pack is included, set lensPackPrice to 0
        lensPackPrice = 0;
    } else if (glassesType === 'frameAndMonofocal' && lensPack === 'basic' && brand.toLowerCase() !== 'seen' && !isKidsGlasses) {
        // 'bronze' is also included on other brands --> when pack is included, set lensPackPrice to 0
        lensPackPrice = 0;
    } else if (glassesType === 'frameAndMultifocal' && (brand.toLowerCase() === 'seen' || isKidsGlasses)) {
        // 'basic' pack price is also included on Seen and 'kids' glasses when MULTIfocal
        // The price of 'basic' needs to be subtracted from every lenspack to make calculations be correct
        const basicMultiPack = getInitialLensPack(opticalData.multifocalLensPacks, 'basic');
        var ref1;
        const basicMultiPackPrice = (ref1 = getPriceOfLensPack(basicMultiPack)) !== null && ref1 !== void 0 ? ref1 : 0;
        var ref2;
        lensPackPrice = ((ref2 = getPriceOfLensPack(selectedPack)) !== null && ref2 !== void 0 ? ref2 : 0) - basicMultiPackPrice;
    } else if (glassesType === 'frameAndMultifocal' && !isKidsGlasses) {
        // 'bronze' pack price is included on non-Seen and non-'kids' glasses when MULTIfocal
        // The price of 'bronze' needs to be subtracted from every lenspack to make calculations be correct
        const basicMultiPack = getInitialLensPack(opticalData.multifocalLensPacks, 'basic');
        var ref3;
        const basicMultiPackPrice = (ref3 = getPriceOfLensPack(basicMultiPack)) !== null && ref3 !== void 0 ? ref3 : 0;
        var ref4;
        lensPackPrice = ((ref4 = getPriceOfLensPack(selectedPack)) !== null && ref4 !== void 0 ? ref4 : 0) - basicMultiPackPrice;
    } else {
        var ref5;
        lensPackPrice = (ref5 = getPriceOfLensPack(selectedPack)) !== null && ref5 !== void 0 ? ref5 : 0;
    }
    return lensPackPrice;
}
function calcSelectedExtrasPrice(extrasData, selectedExtras) {
    const price = 0;
    if (selectedExtras) {
        return extrasData.filter(({ value  })=>selectedExtras.indexOf(value) >= 0
        ).reduce((sum, item)=>sum + item.price || 0
        , 0);
    }
    return price;
}
// HELPERS
function findAndSortBenefits(packs, intl, sorting) {
    const map = sorting.reduce((main, value, index)=>swcHelpers.objectSpread({}, main, {
            [value]: index
        })
    , {});
    return packs.map((pack)=>{
        const benefits = pack.attributes && pack.attributes.filter((attr)=>/(cb[A-Z])\w+/.exec(attr.name) && attr.name in map
        ).sort((a, b)=>map[a.name] - map[b.name]
        ).map((benefit)=>{
            const labelKey = `products.ssp.lenses.${benefit.name}`;
            const label = intl && intl.getLabel && intl.getLabel(labelKey) || '';
            return swcHelpers.objectSpread({}, benefit, {
                label
            });
        });
        if (!benefits) {
            return swcHelpers.objectSpread({}, pack, {
                benefits: []
            });
        }
        return swcHelpers.objectSpread({}, pack, {
            benefits
        });
    });
}
function getInitialLensPack(lensPackData, selector) {
    const selection = lensPackData.find((pack)=>pack.packType === selector
    );
    return selection ? selection : lensPackData[0];
}
function getPriceOfLensPack(lensPack) {
    var ref, ref6, ref7;
    return lensPack === null || lensPack === void 0 ? void 0 : (ref = lensPack.prices) === null || ref === void 0 ? void 0 : (ref6 = ref[0]) === null || ref6 === void 0 ? void 0 : (ref7 = ref6.value) === null || ref7 === void 0 ? void 0 : ref7.centAmount;
}

export { calcGlassesPrice, calcGlassesPriceDifference, calcLensPackPrice, calcSelectedExtrasPrice, findAndSortBenefits, getFrameData, getInitialLensPack, getLensPacks, getPriceOfLensPack };
//# sourceMappingURL=utils.js.map
